/**
 * Admin CCK credits UI (cck-credits-01 / T4): per-user breakdown + manual grant,
 * and coupon-code management (create / enable-disable + redemption history).
 * Rendered as a distinct section in the admin Users page.
 */
import React, { useCallback, useEffect, useState } from 'react';

import { cckCreditsApi } from '../services/api';
import { CckAdminUser, CckCode, CckRedemptionHistory } from '../types';

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const apiError = (err: any, fallback: string): string => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return fallback;
};

// ── per-user breakdown + grant ────────────────────────────────────────────────
const Breakdown: React.FC = () => {
  const [rows, setRows] = useState<CckAdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [grantId, setGrantId] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await cckCreditsApi.adminListUsers(q || undefined);
      setRows(res.users);
    } catch (err: any) {
      setError(apiError(err, 'Failed to load credit breakdown.'));
    } finally {
      setLoading(false);
    }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const submitGrant = async (userId: string) => {
    const amt = parseInt(amount, 10);
    if (!amt || amt <= 0) { setError('Enter a positive credit amount.'); return; }
    setBusy(true); setError(null);
    try {
      await cckCreditsApi.adminGrant(userId, amt, note);
      setGrantId(null); setAmount(''); setNote('');
      await load();
    } catch (err: any) {
      setError(apiError(err, 'Grant failed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginBottom: 26 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 12 }}>
        <h3 style={sectionTitle}>Credits per user</h3>
        <input placeholder="Search user…" value={search} onChange={(e) => setSearch(e.target.value)}
          style={{ ...inputStyle, minWidth: 200, flex: '0 0 auto' }} />
      </div>
      {error && <div style={errorStyle}>{error}</div>}
      {loading ? (
        <div style={{ color: 'var(--text-faint)', padding: 12 }}>Loading…</div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)' }}>
          <table style={tableStyle}>
            <thead>
              <tr>{['User', 'Owned', 'Used', 'Runs', 'Uploads', 'Soonest expiry', 'Grant'].map((h) => (
                <th key={h} style={thStyle}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.user_id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={tdStyle}>{u.username}<div style={{ color: 'var(--text-faint)', fontSize: 11 }}>{u.email}</div></td>
                  <td style={{ ...tdStyle, color: 'var(--amber)', fontWeight: 600 }}>{u.balance.toLocaleString()}</td>
                  <td style={tdStyle}>{u.used.toLocaleString()}</td>
                  <td style={tdStyle}>{u.jobs}</td>
                  <td style={tdStyle}>{u.uploads}</td>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtDate(u.soonest_expires_at)}</td>
                  <td style={tdStyle}>
                    {grantId === u.user_id ? (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input type="number" min={1} placeholder="Amount" value={amount}
                          onChange={(e) => setAmount(e.target.value)} style={{ ...inputStyle, width: 90 }} autoFocus />
                        <input placeholder="Note (optional)" value={note}
                          onChange={(e) => setNote(e.target.value)} style={{ ...inputStyle, width: 130 }} />
                        <button disabled={busy} onClick={() => submitGrant(u.user_id)} style={btnStyle}>Add</button>
                        <button onClick={() => { setGrantId(null); setAmount(''); setNote(''); }} style={btnStyle}>Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => { setGrantId(u.user_id); setAmount(''); setNote(''); }} style={btnStyle}>
                        Grant credits
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} style={{ ...tdStyle, color: 'var(--text-faint)' }}>No users.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── coupon code management ─────────────────────────────────────────────────────
const Codes: React.FC = () => {
  const [codes, setCodes] = useState<CckCode[]>([]);
  const [history, setHistory] = useState<CckRedemptionHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ code: '', credit_value: '200', validity_days: '30', max_redemptions: '', description: '' });

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await cckCreditsApi.adminListCodes();
      setCodes(res.codes); setHistory(res.redemptions);
    } catch (err: any) {
      setError(apiError(err, 'Failed to load codes.'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await cckCreditsApi.adminCreateCode({
        code: form.code.trim(),
        credit_value: parseInt(form.credit_value, 10),
        validity_days: parseInt(form.validity_days, 10) || 30,
        max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions, 10) : null,
        description: form.description.trim() || undefined,
      });
      setForm({ code: '', credit_value: '200', validity_days: '30', max_redemptions: '', description: '' });
      setShowCreate(false);
      await load();
    } catch (err: any) {
      setError(apiError(err, 'Could not create code.'));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (c: CckCode) => {
    setBusy(true); setError(null);
    try {
      await cckCreditsApi.adminSetCodeActive(c.id, !c.active);
      await load();
    } catch (err: any) {
      setError(apiError(err, 'Could not update code.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h3 style={sectionTitle}>Coupon codes</h3>
        <button className="sidebar-new-run-btn" onClick={() => setShowCreate((s) => !s)}>
          {showCreate ? 'Cancel' : '+ New code'}
        </button>
      </div>
      {error && <div style={errorStyle}>{error}</div>}

      {showCreate && (
        <form onSubmit={create} style={createFormStyle}>
          <input placeholder="CODE" required value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })} style={inputStyle} />
          <input type="number" min={1} placeholder="Credits" required value={form.credit_value}
            onChange={(e) => setForm({ ...form, credit_value: e.target.value })} style={{ ...inputStyle, width: 100 }} />
          <input type="number" min={1} placeholder="Valid days" value={form.validity_days}
            onChange={(e) => setForm({ ...form, validity_days: e.target.value })} style={{ ...inputStyle, width: 100 }} />
          <input type="number" min={1} placeholder="Max uses (∞)" value={form.max_redemptions}
            onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} style={{ ...inputStyle, width: 110 }} />
          <input placeholder="Description" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })} style={inputStyle} />
          <button type="submit" disabled={busy} className="sidebar-new-run-btn">Create</button>
        </form>
      )}

      {loading ? (
        <div style={{ color: 'var(--text-faint)', padding: 12 }}>Loading…</div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)', marginBottom: 18 }}>
          <table style={tableStyle}>
            <thead>
              <tr>{['Code', 'Credits', 'Valid', 'Redeemed', 'Status', ''].map((h) => (
                <th key={h} style={thStyle}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <tr key={c.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={tdStyle}>{c.code}{c.description && <div style={{ color: 'var(--text-faint)', fontSize: 11 }}>{c.description}</div>}</td>
                  <td style={tdStyle}>{c.credit_value}</td>
                  <td style={tdStyle}>{c.validity_days}d</td>
                  <td style={tdStyle}>{c.times_redeemed}{c.max_redemptions != null ? ` / ${c.max_redemptions}` : ''}</td>
                  <td style={tdStyle}>
                    <span style={{ color: c.active ? 'var(--category-good-fg)' : 'var(--text-faint)' }}>
                      {c.active ? 'active' : 'disabled'}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <button disabled={busy} onClick={() => toggle(c)} style={btnStyle}>
                      {c.active ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))}
              {codes.length === 0 && (
                <tr><td colSpan={6} style={{ ...tdStyle, color: 'var(--text-faint)' }}>No codes yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {history.length > 0 && (
        <details>
          <summary style={{ cursor: 'pointer', color: 'var(--text-soft)', fontSize: 12, marginBottom: 8 }}>
            Redemption history ({history.length})
          </summary>
          <div style={{ overflowX: 'auto', border: '1px solid var(--border)' }}>
            <table style={tableStyle}>
              <thead><tr>{['Code', 'User', 'Credits', 'When'].map((h) => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={tdStyle}>{h.code}</td>
                    <td style={{ ...tdStyle, fontFamily: 'var(--font-data)', fontSize: 11 }}>{h.user_id}</td>
                    <td style={tdStyle}>{h.credits_granted}</td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtDate(h.redeemed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
};

const AdminCredits: React.FC = () => (
  <div style={{ marginTop: 30, borderTop: '2px solid var(--border)', paddingTop: 20 }}>
    <h2 style={{ margin: '0 0 14px', color: 'var(--amber)', fontFamily: 'var(--font-display)', letterSpacing: '0.04em' }}>
      Credits
    </h2>
    <Breakdown />
    <Codes />
  </div>
);

// ── styles ───────────────────────────────────────────────────────────────────
const sectionTitle: React.CSSProperties = {
  margin: 0, color: 'var(--text-strong)', fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.08em',
};
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '8px 12px', background: 'var(--surface)', color: 'var(--text-soft)',
  fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap',
};
const tdStyle: React.CSSProperties = { padding: '8px 12px', color: 'var(--text)' };
const inputStyle: React.CSSProperties = {
  padding: '7px 9px', background: 'var(--surface-subtle)', color: 'var(--text)',
  border: '1px solid var(--border)', outline: 'none', flex: '1 1 120px', minWidth: 120,
};
const btnStyle: React.CSSProperties = {
  background: 'none', border: '1px solid var(--border)', color: 'var(--text-soft)',
  padding: '4px 8px', cursor: 'pointer', fontSize: 11, whiteSpace: 'nowrap',
};
const createFormStyle: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, padding: 12,
  border: '1px solid var(--border)', background: 'var(--surface)',
};
const errorStyle: React.CSSProperties = {
  marginBottom: 10, padding: '8px 12px', border: '1px solid var(--category-dead-fg)',
  background: 'var(--category-dead-bg)', color: 'var(--category-dead-fg)', fontSize: 13,
};

export default AdminCredits;
